import { useMemo } from 'react';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import type { MessageDescriptor } from '@lingui/core';
import {
  CalendarPlus,
  CalendarRange,
  CheckCircle2,
  PenLine,
  Monitor,
  Smartphone,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate } from '@/utils/dateUtils';
import { ModuleUnavailableHint } from '@/components/auth/ModuleUnavailableHint';
import { useModuleGate } from '@/hooks/useAccess';
import { useEmployeePickerNames } from '@/hooks/useEmployees';
import { RELATIONSHIP_READ_MODULES } from '@/lib/modules';
import { useChildRelationships } from '@/hooks/useChildContactRelationships';
import type { SettlingInPlanDetail } from '@/types/settlingIn';
import {
  buildLifecycleEvents,
  type LifecycleChannel,
  type LifecycleEvent,
} from './buildLifecycleEvents';

const EVENT_ICONS: Record<LifecycleEvent['key'], LucideIcon> = {
  plan_created: CalendarPlus,
  schedule_seeded: CalendarRange,
  staff_completion: CheckCircle2,
  parent_sign_off: PenLine,
};

/**
 * Timeline event labels, keyed by `LifecycleEvent['key']`.
 *
 * Each entry MUST be a literal `msg({ id, message })` call: `lingui extract`
 * reads the source statically, so a plain string (or any computed descriptor) is
 * silently skipped and the id never reaches the catalogues.
 *
 * The ids are the same stable ids `buildLifecycleEvents` puts on `labelId`;
 * `buildLifecycleEvents.test.ts` pins the two together so they cannot drift.
 */
export const EVENT_MESSAGES: Record<LifecycleEvent['key'], MessageDescriptor> = {
  plan_created: msg({ id: 'settlingIn.timeline.event.planCreated', message: 'Plan created' }),
  schedule_seeded: msg({
    id: 'settlingIn.timeline.event.scheduleSeeded',
    message: 'Schedule seeded',
  }),
  staff_completion: msg({
    id: 'settlingIn.timeline.event.staffCompletion',
    message: 'Staff completion recorded',
  }),
  parent_sign_off: msg({
    id: 'settlingIn.timeline.event.parentSignOff',
    message: 'Parent sign-off recorded',
  }),
};

/**
 * Event keys whose actor is legitimately absent, so a missing one must render as
 * nothing rather than as the localized "Unknown" fallback.
 *
 * - `parent_sign_off`: rows signed off before migration 119 carry a null
 *   `sign_off_recorded_by`.
 * - `staff_completion`: rows completed before migration 120 carry a null
 *   `completion_recorded_by`.
 *
 * `plan_created` / `schedule_seeded` always have an actor, so for them "Unknown"
 * stays a genuine data-integrity signal.
 */
const ACTORLESS_EVENT_KEYS: ReadonlySet<LifecycleEvent['key']> = new Set([
  'parent_sign_off',
  'staff_completion',
]);

interface LifecycleTimelineProps {
  plan: SettlingInPlanDetail;
  className?: string;
}

/**
 * Resolves a staff employee id to a display name through the any-module
 * employee picker (#1688). The picker is one cached request for the whole
 * timeline, whatever the number of rows. An id the picker cannot resolve (a
 * deleted employee, a non-employee actor) renders "Unknown", never the raw id.
 */
function ActorName({ actorId }: { actorId?: string }) {
  const { i18n } = useLingui();
  const { names } = useEmployeePickerNames();
  const name = actorId ? names.get(actorId) : undefined;

  if (!name) {
    return (
      <span className="text-muted-foreground">
        {i18n._({ id: 'settlingIn.timeline.unknownActor', message: 'Unknown' })}
      </span>
    );
  }

  return <span>{name}</span>;
}

/**
 * The channel marker for a sign-off.
 *
 * The admin-vs-app distinction rests on THREE independent channels — a distinct
 * icon, distinct localized text, and the badge outline — so it is never carried
 * by colour alone.
 */
function ChannelBadge({ channel }: { channel: LifecycleChannel }) {
  const { i18n } = useLingui();
  const Icon = channel === 'admin' ? Monitor : Smartphone;
  const label =
    channel === 'admin'
      ? i18n._({ id: 'settlingIn.timeline.channel.admin', message: 'Recorded by admin' })
      : i18n._({ id: 'settlingIn.timeline.channel.app', message: 'Signed in the parent app' });

  return (
    <span className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs text-capacity-normal-on-surface">
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

/**
 * Vertical activity timeline for the settling-in detail page (ST-5/S15).
 *
 * Lists plan-created / schedule-seeded / staff-completion / parent-sign-off with
 * actor, timestamp and — for the sign-off — the channel that recorded it, so an
 * admin can answer "who signed this off and how?" without leaving the page.
 *
 * Wrap-tolerant flex with no fixed min-widths: German runs 100–200 % long on
 * these labels.
 */
export function LifecycleTimeline({ plan, className }: LifecycleTimelineProps) {
  const { i18n } = useLingui();
  const events = useMemo(() => buildLifecycleEvents(plan), [plan]);

  // REL_READ (#1670): without it the sign-off contact renders as the role hint.
  const canReadRelationships = useModuleGate(RELATIONSHIP_READ_MODULES);
  const { data: relationships } = useChildRelationships(
    canReadRelationships ? (plan.child_id ?? '') : '',
    canReadRelationships && !!plan.child_id,
  );

  const signOffContactName = useMemo(() => {
    const contactId = events.find((e) => e.key === 'parent_sign_off')?.contactId;
    if (!canReadRelationships || !contactId || !Array.isArray(relationships)) return undefined;
    const match = relationships.find((r: { contactId?: string }) => r.contactId === contactId) as
      | { contactFirstname?: string; contactLastname?: string }
      | undefined;
    if (!match) return undefined;
    return `${match.contactFirstname || ''} ${match.contactLastname || ''}`.trim() || undefined;
  }, [canReadRelationships, relationships, events]);

  if (events.length === 0) return null;

  return (
    <div
      role="group"
      aria-label={i18n._({ id: 'settlingIn.timeline.ariaLabel', message: 'Lifecycle activity' })}
      data-testid="lifecycle-timeline"
      className={cn('rounded-lg border bg-card p-4', className)}
    >
      {/*
        An H2, not an H3: this is a top-level section directly under the page's
        H1, and an H1 → H3 jump is an axe `heading-order` violation. With this
        H2 in place the page reads H1 → H2 → H3 (the closure/section cards).
      */}
      <h2 className="text-sm font-semibold mb-3">
        {i18n._({ id: 'settlingIn.timeline.title', message: 'Lifecycle activity' })}
      </h2>

      <ol className="space-y-3">
        {events.map((event) => {
          const Icon = EVENT_ICONS[event.key];
          return (
            <li key={event.key} className="flex flex-wrap items-start gap-2">
              <Icon
                className="h-4 w-4 shrink-0 mt-0.5 text-capacity-normal-on-surface"
                aria-hidden="true"
              />
              <div className="flex flex-col gap-1 min-w-0">
                <span className="text-sm leading-tight">
                  {i18n._(EVENT_MESSAGES[event.key])}
                </span>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                  {event.timestamp && <span>{formatDate(event.timestamp, i18n.locale)}</span>}
                  {/* The sign-off additionally names the signing CONTACT; every
                      event names the acting staff member where one is recorded
                      (see ACTORLESS_EVENT_KEYS). */}
                  {event.key === 'parent_sign_off' && signOffContactName && (
                    <span>{signOffContactName}</span>
                  )}
                  {event.key === 'parent_sign_off' && event.contactId && !canReadRelationships && (
                    <ModuleUnavailableHint variant="inline" />
                  )}
                  {ACTORLESS_EVENT_KEYS.has(event.key) ? (
                    event.actorId ? <ActorName actorId={event.actorId} /> : null
                  ) : (
                    <ActorName actorId={event.actorId} />
                  )}
                  {event.channel && <ChannelBadge channel={event.channel} />}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
